import React, { useEffect, useRef, useState } from 'react';
import { getBackendUrl } from '@/config';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { toast } from 'sonner';

export const AuthCallback = () => {
  const navigate = useNavigate();
  const { updateUser } = useAuth();
  const hasProcessed = useRef(false);
  const [error, setError] = useState(null);
  const [status, setStatus] = useState('Initializing...');

  useEffect(() => {
    // Prevent double processing in StrictMode
    if (hasProcessed.current) return;
    hasProcessed.current = true;

    const processAuth = async () => {
      try {
        // Extract session_id from URL fragment
        const hash = window.location.hash;
        console.log('Auth callback - URL hash:', hash);
        setStatus('Extracting session information...');
        
        const sessionId = hash.split('session_id=')[1]?.split('&')[0];
        console.log('Auth callback - session_id:', sessionId);

        if (!sessionId) {
          setError('No session ID found in URL');
          toast.error('Authentication failed - no session ID');
          setTimeout(() => navigate('/login'), 2000);
          return;
        }

        // Exchange session_id for user data from Emergent Auth
        setStatus('Validating session with authentication server...');
        console.log('Auth callback - Fetching from Emergent Auth...');
        
        let emergentData;
        try {
          const emergentResponse = await fetch('https://demobackend.emergentagent.com/auth/v1/env/oauth/session-data', {
            headers: {
              'X-Session-ID': sessionId
            }
          });

          if (!emergentResponse.ok) {
            const errorText = await emergentResponse.text();
            console.error('Emergent Auth error response:', emergentResponse.status, errorText);
            throw new Error(`Session validation failed (${emergentResponse.status}): ${errorText || 'Unknown error'}`);
          }

          emergentData = await emergentResponse.json();
          console.log('Auth callback - Emergent data received:', emergentData);
          
          if (!emergentData.email) {
            throw new Error('No email received from authentication server');
          }
        } catch (emergentError) {
          console.error('Emergent Auth fetch error:', emergentError);
          throw new Error(`Failed to validate session: ${emergentError.message}`);
        }

        // Send to our backend to create/update user and get JWT token
        setStatus('Creating your account session...');
        const backendUrl = getBackendUrl();
        console.log('Auth callback - Sending to backend:', backendUrl);
        
        let result;
        try {
          const backendResponse = await fetch(`${backendUrl}/api/auth/google/callback`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json'
            },
            body: JSON.stringify({
              email: emergentData.email,
              name: emergentData.name || emergentData.email.split('@')[0],
              picture: emergentData.picture || null,
              session_token: emergentData.session_token || sessionId
            })
          });

          if (!backendResponse.ok) {
            const errorData = await backendResponse.json().catch(() => ({ detail: 'Unknown backend error' }));
            console.error('Backend error:', backendResponse.status, errorData);
            throw new Error(errorData.detail || `Backend error (${backendResponse.status})`);
          }

          result = await backendResponse.json();
          console.log('Auth callback - Backend response received:', result);
        } catch (backendError) {
          console.error('Backend fetch error:', backendError);
          throw new Error(`Failed to create session: ${backendError.message}`);
        }

        // Store token and user data
        setStatus('Completing sign in...');
        localStorage.setItem('access_token', result.access_token);
        localStorage.setItem('user', JSON.stringify(result.user));

        // Update auth context
        if (updateUser) {
          updateUser(result.user);
        }

        // Set flag to skip delay in ProtectedRoute
        sessionStorage.setItem('just_authenticated', 'true');

        // Clear URL fragment
        window.history.replaceState(null, '', window.location.pathname);

        toast.success('Successfully signed in with Google!');

        // Navigate based on role
        const role = result.user.role;
        console.log('Auth callback - Navigating based on role:', role);
        
        if (role === 'admin') {
          navigate('/admin/dashboard', { replace: true });
        } else if (role === 'agent') {
          navigate('/agent/dashboard', { replace: true });
        } else if (role === 'agency') {
          navigate('/agency/dashboard', { replace: true });
        } else {
          navigate('/dashboard', { replace: true });
        }

      } catch (error) {
        console.error('Auth callback error:', error);
        setError(error.message);
        toast.error(`Authentication failed: ${error.message}`);
        setTimeout(() => navigate('/login'), 4000);
      }
    };

    processAuth();
  }, [navigate, updateUser]);

  if (error) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-gradient-to-br from-red-50 to-background">
        <div className="text-center max-w-md p-6">
          <div className="text-red-500 text-xl mb-4 font-semibold">Authentication Error</div>
          <p className="text-muted-foreground mb-4">{error}</p>
          <p className="text-sm text-muted-foreground">Redirecting to login page...</p>
          <button 
            onClick={() => navigate('/login')}
            className="mt-4 px-4 py-2 bg-primary text-primary-foreground rounded-md hover:bg-primary/90"
          >
            Go to Login Now
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="flex items-center justify-center min-h-screen bg-gradient-to-br from-primary/5 via-background to-secondary/5">
      <div className="text-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
        <p className="text-muted-foreground">{status}</p>
      </div>
    </div>
  );
};
